import React from 'react';
import { HBox, VBox } from '../layout';
import { PersonImage } from '../basic';
import { intl } from '../../lib';
import type { ccModel, icModel } from 'generated-ts-common';
import {
  BackServiceTypeEnum,
  formatHPDAddresses,
  getAppointmentResourceAndCenter,
  getSpecialtyFromAppointment,
  getValidArrayOrUndefined,
  isBackendServiceSked,
  splitName
} from '../../util';

type AppointmentSchedulingServiceType = ccModel.portal.config.appointments.AppointmentSchedulingServiceType;
type Appointment = icModel.saf.infrastructure.model.appointments.Appointment;

/**
 * Props for a {@link PhysicianAvatarWithInfo} component.
 */
export interface PhysicianAvatarWithInfoProps {
  /**
   * The first name of the physician.
   */
  firstName?: string;

  /**
   * The last name of the physician.
   */
  lastName?: string;

  /**
   * The specialization of the physician.
   */
  specialization?: string;

  /**
   * Additional CSS class names for custom styling of the component.
   */
  className?: string;

  /**
   * Additional CSS class names for custom styling of the physician's name.
   */
  classNamePhysicianName?: string;

  /**
   * Additional CSS class names for custom styling of the avatar info section.
   */
  classAvatarInfo?: string;

  /**
   * The address of the physician.
   */
  address?: string;

  /**
   * Image to display
   */
  image?: string;

  /**
   * The appointment to extract details from.
   */
  appointment?: Appointment;
  /**
   * The backendService type to extract right resource and center.
   */
  backendService?: AppointmentSchedulingServiceType;

  /**
   * Custom address content to be displayed instead of the default address.
   */
  customAddressContent?: React.ReactNode;
}

/**
 * Component to show details of Physician.
 * Details can be pass individually or pass by appointment if individual details are not provided.
 *
 * @return the created {@link React.JSX.Element}.
 */
export function PhysicianAvatarWithInfo({
  firstName,
  lastName,
  specialization,
  className = '',
  classNamePhysicianName = '',
  classAvatarInfo = '',
  address,
  image,
  appointment,
  backendService = BackServiceTypeEnum.HDR,
  customAddressContent,
  ...props
}: Readonly<PhysicianAvatarWithInfoProps>): React.JSX.Element {
  // if appointment is passed, details are extracted to these variables
  // and used if individual detail props are not passed
  let resourceApt, centerApt, specializationApt, addressApt;

  if (appointment) {
    [resourceApt, centerApt] = getAppointmentResourceAndCenter(
      appointment.participants,
      isBackendServiceSked(backendService)
    );

    specializationApt = getSpecialtyFromAppointment(appointment)?.displayName;

    if (!(firstName && lastName)) {
      const splitNameResult = splitName(resourceApt?.displayName);
      firstName = splitNameResult.firstName;
      lastName = splitNameResult.lastName;
    }
    addressApt = formatHPDAddresses(
      getValidArrayOrUndefined(centerApt?.addresses) ||
        getValidArrayOrUndefined(resourceApt?.addresses),
      false
    );
  }

  return (
    <HBox
      {...props}
      className={`ehs-physician-avatar-with-info-container ${className}`}
    >
      <PersonImage
        image={image ?? resourceApt?.image}
        emptyState={!firstName && !lastName ? 'defaultAvatar' : 'nameCircle'}
        firstName={firstName as string}
        lastName={lastName as string}
        className="avatar-image"
      />

      <VBox className={`avatar-info ${classAvatarInfo}`}>
        {(!!firstName || !!lastName) && (
          <p className={`${classNamePhysicianName} text-90 bold`}>
            {`${firstName} ${lastName}`}
          </p>
        )}
        {!!(specialization ?? specializationApt) && (
          <p>
            <span className="sr-only">
              {intl('appointmentSchedulingPortlet.filter.service')}
            </span>
            <span className="text-90">
              {specialization ?? specializationApt}
            </span>
          </p>
        )}
        {customAddressContent ? (
          <>{customAddressContent}</>
        ) : (
          !!(address ?? addressApt) && (
            <p>
              <span className="sr-only">{intl('common.address')}</span>
              <span className="address text-90">{address ?? addressApt}</span>
            </p>
          )
        )}
      </VBox>
    </HBox>
  );
}
